<template>
    <UiModal
        v-if="!isMobile"
        :isOpen="show && !showDeleteModal"
        @close="emit('close')"
        width="600px"
        :fullScreenMobile="true"
    >
        <template #title>
            <div>
                <h3 class="text-xl font-medium leading-7 text-slate-800 dark:text-slate-100 mb-1">
                    Manage sender emails
                </h3>
                <p class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400">
                    Email addresses that can forward redemptions to your account
                </p>
                <p class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 mt-1">
                    Note: You cannot add another My10x account email as a
                    sender.
                </p>
            </div>
        </template>

        <template #content>
            <div class="flex flex-col items-start gap-4 w-full">
                <!-- Email list -->
                <div class="w-full">
                    <!-- Primary email -->
                    <div class="flex py-3 items-center gap-2 self-stretch">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            class="shrink-0"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M2.5 5.83333C2.5 4.45262 3.61929 3.33333 5 3.33333H15C16.3807 3.33333 17.5 4.45262 17.5 5.83333V14.1667C17.5 15.5474 16.3807 16.6667 15 16.6667H5C3.61929 16.6667 2.5 15.5474 2.5 14.1667V5.83333ZM5 5H15C15.2487 5 15.4713 5.10528 15.6278 5.27639L10 9.55556L4.37222 5.27639C4.52869 5.10528 4.75127 5 5 5ZM4.16667 7.05556V14.1667C4.16667 14.6269 4.53976 15 5 15H15C15.4602 15 15.8333 14.6269 15.8333 14.1667V7.05556L10.4428 11.1481C10.1809 11.347 9.81908 11.347 9.55719 11.1481L4.16667 7.05556Z"
                                fill="#475569"
                            />
                        </svg>
                        <span
                            class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100"
                        >
                            {{ userEmail }}
                        </span>
                        <span
                            class="text-sm font-normal leading-5 text-slate-400 ml-1"
                        >
                            (your account)
                        </span>
                    </div>

                    <!-- Additional forwarding emails -->
                    <div
                        v-for="(emailItem, index) in forwardingEmails"
                        :key="emailItem.id"
                        class="flex py-3 items-center gap-2 self-stretch flex-wrap sm:flex-nowrap"
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            class="shrink-0"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M2.5 5.83333C2.5 4.45262 3.61929 3.33333 5 3.33333H15C16.3807 3.33333 17.5 4.45262 17.5 5.83333V14.1667C17.5 15.5474 16.3807 16.6667 15 16.6667H5C3.61929 16.6667 2.5 15.5474 2.5 14.1667V5.83333ZM5 5H15C15.2487 5 15.4713 5.10528 15.6278 5.27639L10 9.55556L4.37222 5.27639C4.52869 5.10528 4.75127 5 5 5ZM4.16667 7.05556V14.1667C4.16667 14.6269 4.53976 15 5 15H15C15.4602 15 15.8333 14.6269 15.8333 14.1667V7.05556L10.4428 11.1481C10.1809 11.347 9.81908 11.347 9.55719 11.1481L4.16667 7.05556Z"
                                fill="#475569"
                            />
                        </svg>
                        <span
                            class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 truncate min-w-0"
                        >
                            {{ emailItem.email }}
                        </span>
                        <!-- Pending badge -->
                        <div
                            v-if="!emailItem.verified"
                            class="flex px-2 py-0.5 justify-center items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700"
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M8.00004 2.66683C5.05452 2.66683 2.66671 5.05464 2.66671 8.00016C2.66671 10.9457 5.05452 13.3335 8.00004 13.3335C10.9456 13.3335 13.3334 10.9457 13.3334 8.00016C13.3334 5.05464 10.9456 2.66683 8.00004 2.66683ZM1.33337 8.00016C1.33337 4.31826 4.31814 1.3335 8.00004 1.3335C11.6819 1.3335 14.6667 4.31826 14.6667 8.00016C14.6667 11.6821 11.6819 14.6668 8.00004 14.6668C4.31814 14.6668 1.33337 11.6821 1.33337 8.00016ZM8.00004 4.66683C8.36823 4.66683 8.66671 4.96531 8.66671 5.3335V7.72402L10.1381 9.19543C10.3985 9.45578 10.3985 9.87789 10.1381 10.1382C9.87776 10.3986 9.45565 10.3986 9.1953 10.1382L7.52864 8.47157C7.40361 8.34654 7.33337 8.17697 7.33337 8.00016V5.3335C7.33337 4.96531 7.63185 4.66683 8.00004 4.66683Z"
                                    fill="#64748B"
                                />
                            </svg>
                            <span
                                class="text-xs font-medium leading-4 text-slate-500 dark:text-slate-400"
                            >
                                Pending
                            </span>
                        </div>
                        <div class="flex-1"></div>
                        <!-- Actions -->
                        <div class="flex items-center gap-4">
                            <!-- Resend -->
                            <button
                                v-if="!emailItem.verified"
                                @click="resendVerification(emailItem)"
                                :disabled="resendingId === emailItem.id"
                                class="flex items-center gap-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:hover:text-slate-300 transition-colors rounded"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="shrink-0"
                                >
                                    <g clip-path="url(#clip0_resend_modal)">
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M2.5 10C2.5 14.1421 5.85786 17.5 10 17.5C11.9017 17.5 13.6687 16.7913 15.0099 15.6213V16.6667C15.0099 17.1269 15.383 17.5 15.8432 17.5C16.3034 17.5 16.6765 17.1269 16.6765 16.6667V13.9583C16.6765 13.1529 16.0236 12.5 15.2182 12.5L12.5099 12.5C12.0496 12.5 11.6765 12.8731 11.6765 13.3333C11.6765 13.7936 12.0496 14.1667 12.5099 14.1667H14.131C13.0634 15.1965 11.5921 15.8333 10 15.8333C6.77834 15.8333 4.16667 13.2217 4.16667 10C4.16667 9.53976 3.79357 9.16667 3.33333 9.16667C2.8731 9.16667 2.5 9.53976 2.5 10ZM16.6667 10.8333C17.1269 10.8333 17.5 10.4602 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C8.10298 2.5 6.33993 3.2052 5 4.37007V3.33333C5 2.8731 4.6269 2.5 4.16667 2.5C3.70643 2.5 3.33333 2.8731 3.33333 3.33333L3.33333 6.04167C3.33333 6.84708 3.98625 7.5 4.79167 7.5H7.29167C7.7519 7.5 8.125 7.1269 8.125 6.66667C8.125 6.20643 7.7519 5.83333 7.29167 5.83333H5.86903C6.93664 4.80345 8.4079 4.16667 10 4.16667C13.2217 4.16667 15.8333 6.77834 15.8333 10C15.8333 10.4602 16.2064 10.8333 16.6667 10.8333Z"
                                            fill="#1E293B"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_resend_modal">
                                            <rect
                                                width="20"
                                                height="20"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                                <span class="hidden sm:inline">Resend</span>
                            </button>
                            <!-- Delete -->
                            <button
                                @click="confirmDeleteEmail(emailItem)"
                                :disabled="removingId === emailItem.id"
                                class="flex items-center gap-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:hover:text-slate-300 transition-colors rounded"
                            >
                                <Icon
                                    icon="trash"
                                    :width="20"
                                    :height="20"
                                    svg-class="text-slate-800 dark:text-slate-100"
                                />
                                <span class="hidden sm:inline">Delete</span>
                            </button>
                        </div>
                    </div>

                    <!-- Add email form (inline) -->
                    <div
                        v-if="showAddForm"
                        class="flex pt-2 items-center w-full"
                    >
                        <div class="flex items-stretch gap-2 flex-1">
                            <UiInput
                                id="modal-new-email"
                                v-model:input-value="newEmail"
                                type="email"
                                placeholder="Enter email"
                                :error="emailError"
                                :options="{
                                    hideLabel: true,
                                    wrapperClass: 'flex-1',
                                }"
                                @on-input="emailError = ''"
                                @keyup.enter="addForwardingEmail"
                            />
                            <button
                                @click="cancelAddForm"
                                class="flex items-center justify-center w-10 shrink-0 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M12 4L4 12M4 4L12 12"
                                        stroke="#1E293B"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </button>
                            <button
                                @click="addForwardingEmail"
                                :disabled="!newEmail || addingEmail"
                                class="flex items-center justify-center w-10 shrink-0 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-50 transition-colors"
                            >
                                <svg
                                    v-if="!addingEmail"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M13.3334 4L6.00008 11.3333L2.66675 8"
                                        stroke="white"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                                <i
                                    v-else
                                    class="fas fa-spinner fa-spin text-white text-sm"
                                ></i>
                            </button>
                        </div>
                    </div>
                    <!-- + Add email button -->
                    <div v-if="!showAddForm" class="py-3">
                        <button
                            @click="showAddForm = true"
                            class="flex items-center gap-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:hover:text-slate-300 transition-colors rounded"
                        >
                            <span class="text-base leading-none">+</span>
                            Add email
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </UiModal>

    <UiModalMobile
        v-if="isMobile"
        :isOpen="show && !showDeleteModal"
        @close="emit('close')"
        title="Manage sender emails"
    >
        <template #content>
            <p class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 mb-1">
                Email addresses that can forward redemptions to your account
            </p>
            <p class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 mb-4">
                Note: You cannot add another My10x account email as a sender.
            </p>

            <div class="w-full">
                <!-- Primary email -->
                <div class="flex py-3 items-center gap-2 self-stretch">
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="shrink-0"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M2.5 5.83333C2.5 4.45262 3.61929 3.33333 5 3.33333H15C16.3807 3.33333 17.5 4.45262 17.5 5.83333V14.1667C17.5 15.5474 16.3807 16.6667 15 16.6667H5C3.61929 16.6667 2.5 15.5474 2.5 14.1667V5.83333ZM5 5H15C15.2487 5 15.4713 5.10528 15.6278 5.27639L10 9.55556L4.37222 5.27639C4.52869 5.10528 4.75127 5 5 5ZM4.16667 7.05556V14.1667C4.16667 14.6269 4.53976 15 5 15H15C15.4602 15 15.8333 14.6269 15.8333 14.1667V7.05556L10.4428 11.1481C10.1809 11.347 9.81908 11.347 9.55719 11.1481L4.16667 7.05556Z"
                            fill="#475569"
                        />
                    </svg>
                    <span class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100">
                        {{ userEmail }}
                    </span>
                    <span
                        class="text-sm font-normal leading-5 text-slate-400 ml-1"
                    >
                        (your account)
                    </span>
                </div>

                <!-- Additional forwarding emails -->
                <div
                    v-for="(emailItem, index) in forwardingEmails"
                    :key="emailItem.id"
                    class="flex py-3 items-center gap-2 self-stretch flex-wrap"
                >
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="shrink-0"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M2.5 5.83333C2.5 4.45262 3.61929 3.33333 5 3.33333H15C16.3807 3.33333 17.5 4.45262 17.5 5.83333V14.1667C17.5 15.5474 16.3807 16.6667 15 16.6667H5C3.61929 16.6667 2.5 15.5474 2.5 14.1667V5.83333ZM5 5H15C15.2487 5 15.4713 5.10528 15.6278 5.27639L10 9.55556L4.37222 5.27639C4.52869 5.10528 4.75127 5 5 5ZM4.16667 7.05556V14.1667C4.16667 14.6269 4.53976 15 5 15H15C15.4602 15 15.8333 14.6269 15.8333 14.1667V7.05556L10.4428 11.1481C10.1809 11.347 9.81908 11.347 9.55719 11.1481L4.16667 7.05556Z"
                            fill="#475569"
                        />
                    </svg>
                    <span
                        class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 truncate min-w-0"
                    >
                        {{ emailItem.email }}
                    </span>
                    <div
                        v-if="!emailItem.verified"
                        class="flex px-2 py-0.5 justify-center items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700"
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M8.00004 2.66683C5.05452 2.66683 2.66671 5.05464 2.66671 8.00016C2.66671 10.9457 5.05452 13.3335 8.00004 13.3335C10.9456 13.3335 13.3334 10.9457 13.3334 8.00016C13.3334 5.05464 10.9456 2.66683 8.00004 2.66683ZM1.33337 8.00016C1.33337 4.31826 4.31814 1.3335 8.00004 1.3335C11.6819 1.3335 14.6667 4.31826 14.6667 8.00016C14.6667 11.6821 11.6819 14.6668 8.00004 14.6668C4.31814 14.6668 1.33337 11.6821 1.33337 8.00016ZM8.00004 4.66683C8.36823 4.66683 8.66671 4.96531 8.66671 5.3335V7.72402L10.1381 9.19543C10.3985 9.45578 10.3985 9.87789 10.1381 10.1382C9.87776 10.3986 9.45565 10.3986 9.1953 10.1382L7.52864 8.47157C7.40361 8.34654 7.33337 8.17697 7.33337 8.00016V5.3335C7.33337 4.96531 7.63185 4.66683 8.00004 4.66683Z"
                                fill="#64748B"
                            />
                        </svg>
                        <span
                            class="text-xs font-medium leading-4 text-slate-500 dark:text-slate-400"
                        >
                            Pending
                        </span>
                    </div>
                    <div class="flex-1"></div>
                    <div class="flex items-center gap-4">
                        <button
                            v-if="!emailItem.verified"
                            @click="resendVerification(emailItem)"
                            :disabled="resendingId === emailItem.id"
                            class="flex items-center gap-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:hover:text-slate-300 transition-colors rounded"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                class="shrink-0"
                            >
                                <g clip-path="url(#clip0_resend_mobile)">
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M2.5 10C2.5 14.1421 5.85786 17.5 10 17.5C11.9017 17.5 13.6687 16.7913 15.0099 15.6213V16.6667C15.0099 17.1269 15.383 17.5 15.8432 17.5C16.3034 17.5 16.6765 17.1269 16.6765 16.6667V13.9583C16.6765 13.1529 16.0236 12.5 15.2182 12.5L12.5099 12.5C12.0496 12.5 11.6765 12.8731 11.6765 13.3333C11.6765 13.7936 12.0496 14.1667 12.5099 14.1667H14.131C13.0634 15.1965 11.5921 15.8333 10 15.8333C6.77834 15.8333 4.16667 13.2217 4.16667 10C4.16667 9.53976 3.79357 9.16667 3.33333 9.16667C2.8731 9.16667 2.5 9.53976 2.5 10ZM16.6667 10.8333C17.1269 10.8333 17.5 10.4602 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C8.10298 2.5 6.33993 3.2052 5 4.37007V3.33333C5 2.8731 4.6269 2.5 4.16667 2.5C3.70643 2.5 3.33333 2.8731 3.33333 3.33333L3.33333 6.04167C3.33333 6.84708 3.98625 7.5 4.79167 7.5H7.29167C7.7519 7.5 8.125 7.1269 8.125 6.66667C8.125 6.20643 7.7519 5.83333 7.29167 5.83333H5.86903C6.93664 4.80345 8.4079 4.16667 10 4.16667C13.2217 4.16667 15.8333 6.77834 15.8333 10C15.8333 10.4602 16.2064 10.8333 16.6667 10.8333Z"
                                        fill="#1E293B"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_resend_mobile">
                                        <rect
                                            width="20"
                                            height="20"
                                            fill="white"
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                            <span>Resend</span>
                        </button>
                        <button
                            @click="confirmDeleteEmail(emailItem)"
                            :disabled="removingId === emailItem.id"
                            class="flex items-center gap-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:hover:text-slate-300 transition-colors rounded"
                        >
                            <Icon
                                icon="trash"
                                :width="20"
                                :height="20"
                                svg-class="text-slate-800 dark:text-slate-100"
                            />
                            <span>Delete</span>
                        </button>
                    </div>
                </div>

                <!-- Add email form (inline) -->
                <div v-if="showAddForm" class="flex pt-2 items-center w-full">
                    <div class="flex items-start gap-2 flex-1">
                        <UiInput
                            id="modal-new-email-mobile"
                            v-model:input-value="newEmail"
                            type="email"
                            placeholder="Enter email"
                            :error="emailError"
                            :options="{
                                hideLabel: true,
                                wrapperClass: 'flex-1',
                            }"
                            @on-input="emailError = ''"
                            @keyup.enter="addForwardingEmail"
                        />
                        <button
                            @click="cancelAddForm"
                            class="flex items-center justify-center w-10 h-10 shrink-0 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M12 4L4 12M4 4L12 12"
                                    stroke="#1E293B"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </button>
                        <button
                            @click="addForwardingEmail"
                            :disabled="!newEmail || addingEmail"
                            class="flex items-center justify-center w-10 h-10 shrink-0 rounded-full bg-slate-800 hover:bg-slate-700 disabled:opacity-50 transition-colors"
                        >
                            <svg
                                v-if="!addingEmail"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M13.3334 4L6.00008 11.3333L2.66675 8"
                                    stroke="white"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            <i
                                v-else
                                class="fas fa-spinner fa-spin text-white text-sm"
                            ></i>
                        </button>
                    </div>
                </div>

                <!-- + Add email button -->
                <div v-if="!showAddForm" class="py-3">
                    <button
                        @click="showAddForm = true"
                        class="flex items-center gap-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:hover:text-slate-300 transition-colors rounded"
                    >
                        <span class="text-base leading-none">+</span>
                        Add email
                    </button>
                </div>
            </div>
        </template>
    </UiModalMobile>

    <!-- Delete confirmation modal -->
    <UiModal
        :is-open="showDeleteModal"
        @close="showDeleteModal = false"
        :is-teleported="true"
        width="600px"
        :full-screen-mobile="true"
    >
        <template #title>
            <div class="flex items-center">
                <div
                    class="w-12 h-12 rounded-full flex flex-col justify-center items-center mr-3 shrink-0 bg-red-100"
                >
                    <Icon icon="trash" path-class="fill-red-500" />
                </div>
                <h5 class="flex-1 text-xl font-medium leading-7 text-slate-800 dark:text-slate-100">
                    Delete sender email
                </h5>
            </div>
        </template>
        <template #content>
            <div class="relative">
                <div class="space-y-4">
                    <p
                        class="self-stretch text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                    >
                        You are about to delete additional sender email. This
                        action cannot be undone.
                    </p>
                </div>
            </div>
        </template>
        <template #footer>
            <div class="flex items-center justify-end gap-2">
                <button
                    type="button"
                    class="btn btn-secondary"
                    @click="showDeleteModal = false"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="btn btn-danger"
                    @click="handleConfirmDelete"
                >
                    Delete email
                </button>
            </div>
        </template>
    </UiModal>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { Apii } from '@/services/api';
import { notify } from 'notiwind';
import { useIsMobile } from '@/composables/useIsMobile';

const { isMobile } = useIsMobile(640);

const ApiService = new Apii();

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    userEmail: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['close']);

const newEmail = ref('');
const addingEmail = ref(false);
const emailError = ref('');
const resendingId = ref(null);
const removingId = ref(null);
const showAddForm = ref(false);
const forwardingEmails = ref([]);
const showDeleteModal = ref(false);
const emailToDelete = ref(null);

const fetchForwardingEmails = async () => {
    try {
        const response = await ApiService.apiCall(
            '/user/forwarding-emails',
            'get'
        );
        const data = response.data || response;
        forwardingEmails.value = data.forwarding_emails || [];
    } catch (err) {
        console.error('Failed to fetch forwarding emails', err);
    }
};

watch(
    () => props.show,
    (val) => {
        if (val) {
            fetchForwardingEmails();
            showAddForm.value = false;
            newEmail.value = '';
            emailError.value = '';
        }
    }
);

const cancelAddForm = () => {
    showAddForm.value = false;
    newEmail.value = '';
    emailError.value = '';
};

const addForwardingEmail = async () => {
    if (!newEmail.value || addingEmail.value) return;

    emailError.value = '';
    addingEmail.value = true;

    try {
        const response = await ApiService.apiCall(
            '/user/forwarding-emails',
            'post',
            { email: newEmail.value }
        );

        const data = response.data || response;

        if (data.forwarding_email) {
            forwardingEmails.value.push({
                id: data.forwarding_email.id,
                email: data.forwarding_email.email,
                verified: false,
            });
        }

        newEmail.value = '';
        showAddForm.value = false;
        notify(
            {
                group: 'success',
                title: 'Verification Email Sent',
                text: 'Please check your inbox and click the verification link.',
            },
            4000
        );
    } catch (err) {
        const validationError = err.response?.data?.errors?.email?.[0];
        const customError = err.response?.data?.error;
        const errorMessage =
            validationError ||
            customError ||
            'Failed to add email. Please try again.';
        emailError.value = errorMessage;
    } finally {
        addingEmail.value = false;
    }
};

const resendVerification = async (emailItem) => {
    resendingId.value = emailItem.id;

    try {
        await ApiService.apiCall(
            `/user/forwarding-emails/${emailItem.id}/resend`,
            'post'
        );
        notify(
            {
                group: 'success',
                title: 'Verification Email Sent',
                text: `Verification email resent to ${emailItem.email}`,
            },
            4000
        );
    } catch (err) {
        notify(
            {
                group: 'error',
                title: 'Error',
                text: 'Failed to resend verification email. Please try again.',
            },
            4000
        );
    } finally {
        resendingId.value = null;
    }
};

const confirmDeleteEmail = (emailItem) => {
    emailToDelete.value = emailItem;
    showDeleteModal.value = true;
};

const handleConfirmDelete = () => {
    if (emailToDelete.value) {
        removeForwardingEmail(emailToDelete.value);
    }
    showDeleteModal.value = false;
    emailToDelete.value = null;
};

const removeForwardingEmail = async (emailItem) => {
    removingId.value = emailItem.id;

    try {
        await ApiService.apiCall(
            `/user/forwarding-emails/${emailItem.id}`,
            'delete'
        );
        forwardingEmails.value = forwardingEmails.value.filter(
            (e) => e.id !== emailItem.id
        );
        notify(
            {
                group: 'success',
                title: 'Sender email deleted',
                text: 'Additional sender email was successfully deleted.',
            },
            4000
        );
    } catch (err) {
        notify(
            {
                group: 'error',
                title: 'Error',
                text: 'Failed to remove email. Please try again.',
            },
            4000
        );
    } finally {
        removingId.value = null;
    }
};
</script>
